import { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react'
import { motion, AnimatePresence, LayoutGroup, useInView } from 'framer-motion'
import { useIoBroker } from '../hooks/useIoBroker'
import { useAuth } from '../hooks/useAuth'
import { PageCardPicker, CustomizeButton } from './PageCardPicker'
import { useWeather } from '../context/WeatherContext'
import { useLiveData } from '../context/LiveDataContext'
import ErrorBoundary from './ErrorBoundary'
import MqttOverviewBar from './MqttOverviewBar'
import { ALL_CARDS, DEFAULT_CARD_ORDER, MINIMIZED_ID, computeRowGroups } from '../pages/cardRegistry'
import { CardMinimizedProvider } from '../contexts/CardMinimizedContext'
import { CardRemoveProvider } from '../contexts/CardRemoveContext'
import { carCardProps } from './carCardProps'
import { useCardEntrance, SkeletonLayer, CardReveal } from './CardMotion'
import { SPRING } from '../theme/motion'

// All cards are code-split — their JS chunks download only when the card nears the viewport
const HeroCard           = lazy(() => import('./HeroCard'))
const EnergyCard         = lazy(() => import('./EnergyCard'))
const RoomClimateCard    = lazy(() => import('./RoomClimateCard'))
const CarCard            = lazy(() => import('./CarCard'))
const CarFluxCard = lazy(() => import('./CarFluxCard'))
const ApplianceCard      = lazy(() => import('./ApplianceCard'))
const ShutterCard        = lazy(() => import('./ShutterCard'))
const BatteryCard        = lazy(() => import('./BatteryCard'))
const EnergyFlowCard     = lazy(() => import('./EnergyFlowCard'))
const EnergyFlowStudioCard = lazy(() => import('./EnergyFlowStudioCard'))
const EnergyFlowBalanceCard = lazy(() => import('./EnergyFlowBalanceCard'))
const EnergyFlowFluxCard = lazy(() => import('./EnergyFlowFluxCard'))
const BatteryPrismCard = lazy(() => import('./BatteryPrismCard'))
const BatteryGroveCard = lazy(() => import('./BatteryGroveCard'))
const CalendarCard       = lazy(() => import('./CalendarCard'))
const VehicleCard        = lazy(() => import('./VehicleCard'))
const FloorplanCard      = lazy(() => import('./FloorPlan/FloorplanCard'))
const SolarPredictionCard = lazy(() => import('./SolarPredictionCard'))
const SolarROICard       = lazy(() => import('./SolarROICard'))
const MideaACCard        = lazy(() => import('./MideaACCard'))
const GardenCard         = lazy(() => import('./GardenCard'))
const PlantsCard         = lazy(() => import('./PlantsCard'))
const PrinterCard        = lazy(() => import('./PrinterCard'))
const WeekendScout       = lazy(() => import('./WeekendScout'))
const ChartsCard         = lazy(() => import('./ChartsCard'))
const BirdhouseCard      = lazy(() => import('./BirdhouseCard'))
const VibrationCard      = lazy(() => import('./VibrationCard'))
const HeatPumpCard       = lazy(() => import('./HeatPumpCard'))
const ConsumptionCard    = lazy(() => import('./ConsumptionCard'))
const SystemCard         = lazy(() => import('./SystemCard'))
const NasCard            = lazy(() => import('./NasCard'))
const BridgesCard        = lazy(() => import('./BridgesCard'))
const NukiCard           = lazy(() => import('./NukiCard'))
const TimelineCard       = lazy(() => import('./TimelineCard'))
const SmokeDetectorCard  = lazy(() => import('./SmokeDetectorCard'))

const API_BASE = import.meta.env.VITE_API_BASE ?? ''
const ADMIN_EMAIL = 'creasuit@gmail.com'

const MOBILE_COLS = {
  2:  'col-span-2',
  3:  'col-span-3',
  4:  'col-span-4',
  6:  'col-span-6',
  12: 'col-span-12',
}
const DESKTOP_COLS = {
  2:  'lg:col-span-2',
  3:  'lg:col-span-3',
  4:  'lg:col-span-4',
  6:  'lg:col-span-6',
  12: 'lg:col-span-12',
}


// Registry: id → { Component, getProps(cardProps), wrapperClass? }
// MINIMIZED_ID and computeRowGroups are imported from cardRegistry

// Adding a new card = one entry here, no switch case needed.
const CARD_REGISTRY = {
  'car-flux': { Component: CarFluxCard, getProps: ({ charger }) => carCardProps(charger) },
  hero: {
    Component: HeroCard,
    getProps: ({ brightsky, forecast, error }) => ({
      allOk:    !error,
      brightsky,
      forecast,
    }),
  },
  solar: {
    Component: EnergyCard,
    getProps: ({ solar }) => ({ solar }),
  },
  battery: {
    Component: BatteryCard,
    getProps: () => ({}),
  },
  calendar: {
    Component: CalendarCard,
    getProps: () => ({}),
    wrapperClass: 'flex flex-col',
  },
  car: {
    Component: CarCard,
    getProps: ({ charger }) => carCardProps(charger),
  },
  energyflow: {
    Component: EnergyFlowCard,
    getProps: () => ({}),
  },
  'energyflow-studio': { Component: EnergyFlowStudioCard, getProps: () => ({}) },
  'energyflow-balance': { Component: EnergyFlowBalanceCard, getProps: () => ({}) },
  'energyflow-flux': { Component: EnergyFlowFluxCard, getProps: () => ({}) },
  'battery-prism': { Component: BatteryPrismCard, getProps: () => ({}) },
  'battery-grove': { Component: BatteryGroveCard, getProps: () => ({}) },
  climate: {
    Component: RoomClimateCard,
    getProps: ({ tado1Rooms, tado0Rooms }) => ({ tado1Rooms, tado0Rooms }),
  },
  charts: {
    Component: ChartsCard,
    getProps: () => ({}),
  },
  shutters: {
    Component: ShutterCard,
    getProps: () => ({}),
  },
  appliances: {
    Component: ApplianceCard,
    getProps: ({ appliances }) => ({ appliances }),
  },
  vehicle: {
    Component: VehicleCard,
    getProps: ({ charger }) => ({ charger }),
  },
  floorplan: {
    Component: FloorplanCard,
    getProps: () => ({}),
  },
  prediction: {
    Component: SolarPredictionCard,
    getProps: () => ({}),
  },
  solarroi: {
    Component: SolarROICard,
    getProps: () => ({}),
  },
  'midea-ac': {
    Component: MideaACCard,
    getProps: ({ appliances }) => {
      const devices = appliances?.mideaDevices ?? (appliances?.midea ? [appliances.midea] : [])
      return { mideaDevices: devices }
    },
  },
  garden: {
    Component: GardenCard,
    getProps: ({ garden }) => ({ garden }),
  },
  plants: {
    Component: PlantsCard,
    getProps: ({ plants }) => ({ plants }),
  },
  'weekend-scout': {
    Component: WeekendScout,
    getProps: () => ({}),
  },
  printers: {
    Component: PrinterCard,
    getProps: () => ({}),
  },
  birdhouse: {
    Component: BirdhouseCard,
    getProps: () => ({}),   // self-fetches from the camera; no dashboard data slice
  },
  tako: {
    Component: VibrationCard,
    getProps: () => ({}),   // self-fetches gang state + buzzes via /api/tako
  },
  heatpump: {
    Component: HeatPumpCard,
    getProps: ({ heatpump }) => ({ heatpump }),
  },
  consumption: {
    Component: ConsumptionCard,
    getProps: () => ({}),   // reads the breakdown from DataContext
  },
  // Cards native to other pages — renderable here too, since the picker
  // offers every card in ALL_CARDS_REGISTRY on every page.
  system:   { Component: SystemCard,        getProps: () => ({}) },
  nas:      { Component: NasCard,           getProps: () => ({}) },
  bridges:  { Component: BridgesCard,       getProps: () => ({}) },
  nuki:     { Component: NukiCard,          getProps: () => ({}) },
  timeline: { Component: TimelineCard,      getProps: () => ({}) },
  smoke:    { Component: SmokeDetectorCard, getProps: () => ({}) },
}

function W({ children, id, cols, mobileCols, tabletCols, desktopCols, mobileHidden, tabletHidden, desktopHidden, className = '', tabletMode = false, ready = true }) {
  const ref = useRef(null)
  const hasEntered = useInView(ref, { once: true, margin: '400px 0px' })
  const entrance = useCardEntrance(ref)
  const [revealed, setRevealed] = useState(false)

  const tabletWidth = useMemo(() => {
    const c = Number(tabletCols ?? cols ?? 12) || 12
    const baseWidth = (c / 12) * 85
    const finalWidth = Math.max(15, Math.min(90, baseWidth))
    return `${finalWidth}vw`
  }, [cols, tabletCols])

  const visibilityClass = useMemo(() => {
    if (tabletMode) return ''
    if (mobileHidden && desktopHidden) return 'hidden'
    if (mobileHidden) return 'hidden lg:block'
    if (desktopHidden) return 'block lg:hidden'
    return ''
  }, [tabletMode, mobileHidden, desktopHidden])

  if (tabletMode && tabletHidden) return null

  const tabletProps = tabletMode
    ? {
        initial:     { opacity: 0.4, scale: 0.9, originY: 0, filter: 'blur(2px)' },
        whileInView: { opacity: 1,   scale: 1,   originY: 0, filter: 'blur(0px)' },
        viewport:    { amount: 0.2 },
        transition:  { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
        style:       { 
          scrollSnapAlign: 'start', 
          flexShrink: 0, 
          width: tabletWidth,
          minHeight: '200px',
          transformOrigin: 'top center',
          alignSelf: 'flex-start'
        },
        className:   `${className}`,
      }
    : {
        ...entrance,
        // Reorders, added/removed cards and height changes above glide
        // (FLIP) instead of jumping; position only, so content never stretches
        layout:      'position',
        transition:  { layout: SPRING },
        style:       { contain: 'layout paint' },
        className:   `${visibilityClass} ${MOBILE_COLS[mobileCols] ?? 'col-span-12'} ${DESKTOP_COLS[desktopCols] ?? DESKTOP_COLS[cols] ?? 'lg:col-span-12'} h-full ${className}`,
      }

  return (
    <ErrorBoundary>
      <motion.div ref={ref} id={id} {...tabletProps}>
        {/* Reserve a realistic card height so placeholders don't stack as thin
            strips — keeps the in-view math honest (fewer chunks load eagerly)
            and removes the height-jump when the real card mounts. */}
        <div className="relative h-full" style={{ minHeight: 280 }}>
          {/* Unmounted once the card is in — no shimmer loop behind it */}
          {!revealed && <SkeletonLayer />}
          <Suspense fallback={null}>
            {hasEntered && ready && <CardReveal onRevealed={() => setRevealed(true)}>{children}</CardReveal>}
          </Suspense>
        </div>
      </motion.div>
    </ErrorBoundary>
  )
}

function ConnectionBadge({ loading, error, lastUpdated, isTabletMode = false }) {
  if (loading && !lastUpdated) return null
  return (
    <div
      id="dashboard-connection-badge"
      className={`flex items-center gap-2 px-4 py-1.5 rounded-full ${isTabletMode ? 'absolute top-[-4px] left-5 z-10 scale-90 origin-left' : 'lg:col-span-12 -mb-1'}`}
      style={{ background: '#1b1c1b', width: 'fit-content' }}
    >

      {error ? (
        <>
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#ffb4ab' }} />
          <span className="font-label text-[10px] uppercase tracking-wider" style={{ color: '#ffb4ab' }}>
            ioBroker offline
          </span>
        </>
      ) : (
        <>
          <motion.span
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: '#b0ceae' }}
          />
          <span className="font-label text-[10px] uppercase tracking-wider text-outline">
            Live · {lastUpdated?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        </>
      )}
    </div>
  )
}

export default function Dashboard({ isTabletMode = false }) {
  const { data, loading, error, lastUpdated } = useIoBroker()
  const { brightsky, forecast } = useWeather()
  const { battery } = useLiveData()
  const { user } = useAuth()
  const isAdmin = user?.email === ADMIN_EMAIL
  const [cardOrder, setCardOrder] = useState(null)
  const [cardConfigs, setCardConfigs] = useState({})
  const [pickerOpen, setPickerOpen] = useState(false)
  const [revealTimeout, setRevealTimeout] = useState(false)
  const pickerAnchorRef = useRef(null)

  // Hold cards in their placeholder until live data lands, so they fade in
  // populated rather than empty-then-snapping. Fallback after 4s so a stuck or
  // never-arriving stream never leaves cards hidden forever.
  useEffect(() => {
    const t = setTimeout(() => setRevealTimeout(true), 4000)
    return () => clearTimeout(t)
  }, [])
  const dataReady = !loading || revealTimeout

  useEffect(() => {
    fetch(`${API_BASE}/settings`)
      .then(r => r.json())
      .then(s => {
        const saved = Array.isArray(s.dashboard?.cardOrder) ? s.dashboard.cardOrder : DEFAULT_CARD_ORDER
        setCardOrder(saved)
        setCardConfigs(s.dashboard?.cardConfigs ?? {})
      })
      .catch(() => setCardOrder(DEFAULT_CARD_ORDER))
  }, [])

  function save(newOrder, newConfigs) {
    fetch(`${API_BASE}/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dashboard: { cardOrder: newOrder, cardConfigs: newConfigs } }),
    }).catch(() => {})
  }

  function addCard(id) {
    const newOrder = [...(cardOrder ?? []), id]
    setCardOrder(newOrder)
    save(newOrder, cardConfigs)
  }

  function removeCard(id) {
    const newOrder = (cardOrder ?? []).filter(i => i !== id)
    setCardOrder(newOrder)
    save(newOrder, cardConfigs)
  }

  function reorderCards(newOrder) {
    setCardOrder(newOrder)
    save(newOrder, cardConfigs)
  }

  function updateCardConfig(id, key, cols) {
    const newConfigs = { ...cardConfigs, [id]: { ...(cardConfigs[id] ?? {}), [key]: cols } }
    setCardConfigs(newConfigs)
    save(cardOrder ?? [], newConfigs)
  }


  const colsById   = useMemo(() => Object.fromEntries(ALL_CARDS.map(c => [c.id, c.cols])), [])

  const rowGroups = useMemo(
    () => cardOrder ? computeRowGroups(cardOrder, cardConfigs) : [],
    [cardOrder, cardConfigs]
  )
  const solar      = useMemo(() => data?.solar ?? {},      [data])
  const weather    = useMemo(() => data?.weather ?? {},    [data])
  const appliances = useMemo(() => data?.appliances ?? {}, [data])
  const tado1Rooms = useMemo(() => data?.tado1Rooms ?? [], [data])
  const tado0Rooms = useMemo(() => data?.tado0Rooms ?? [], [data])
  const charger    = useMemo(() => ({
    ...data?.charger ?? {},
    maxBatteryDischargeThreshold: battery?.maxDischarge ?? data?.charger?.maxBatteryDischargeThreshold ?? 0,
  }), [data, battery])
  const garden     = useMemo(() => data?.garden ?? {},       [data])
  const plants     = useMemo(() => data?.plants ?? {},       [data])
  const heatpump   = useMemo(() => data?.heatpump ?? {},     [data])
  const cardProps  = useMemo(
    () => ({ solar, weather, appliances, tado1Rooms, tado0Rooms, charger, brightsky, forecast, error, garden, plants, heatpump }),
    [solar, weather, appliances, tado1Rooms, tado0Rooms, charger, brightsky, forecast, error, garden, plants, heatpump]
  )

  if (!cardOrder) return null

  const containerClass = isTabletMode
    ? 'flex flex-nowrap overflow-x-auto scroll-smooth snap-x snap-mandatory tablet-carousel gap-5 items-start'
    : 'grid grid-cols-12 gap-5 pt-4 items-stretch'

  return (
    <div className={`relative ${isTabletMode ? 'pt-4' : 'pt-3'}`}>
      {isAdmin && !isTabletMode && (
        <div className="flex justify-end mb-3" ref={pickerAnchorRef} style={{ position: 'relative' }}>
          <CustomizeButton pickerOpen={pickerOpen} onClick={() => setPickerOpen(o => !o)} />
          <AnimatePresence>
            {pickerOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.97 }}
                transition={{ duration: 0.15 }}
              >
                <PageCardPicker
                  order={cardOrder ?? []}
                  onAdd={addCard}
                  onRemove={removeCard}
                  onReorder={reorderCards}
                  cardConfigs={cardConfigs}
                  onConfigChange={updateCardConfig}
                  onClose={() => setPickerOpen(false)}
                  isAdmin={isAdmin}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      <MqttOverviewBar isTabletMode={isTabletMode} />
      <CardRemoveProvider onRemove={removeCard}>
      <CardMinimizedProvider rowGroups={rowGroups} syncRows={!isTabletMode}>
      {/* One group: when any cell changes, every cell re-measures and glides */}
      <LayoutGroup>
      <div className={containerClass}>
        {cardOrder.map(id => {
          const entry = CARD_REGISTRY[id]
          if (!entry) return null
          const { Component, getProps, wrapperClass = '' } = entry
          const mobileCols = cardConfigs[id]?.mobileCols ?? 12
          const tabletCols = cardConfigs[id]?.tabletCols ?? colsById[id] ?? 12
          const desktopCols = cardConfigs[id]?.desktopCols ?? colsById[id] ?? 12
          
          const mobileHidden = cardConfigs[id]?.mobileHidden ?? false
          const tabletHidden = cardConfigs[id]?.tabletHidden ?? false
          const desktopHidden = cardConfigs[id]?.desktopHidden ?? false

          return (
            <W
              key={id}
              id={`card-${id}`}
              cols={colsById[id] ?? 12}
              mobileCols={mobileCols}
              tabletCols={tabletCols} 
              desktopCols={desktopCols}
              mobileHidden={mobileHidden}
              tabletHidden={tabletHidden}
              desktopHidden={desktopHidden}
              className={wrapperClass}
              tabletMode={isTabletMode}
              ready={dataReady}
            >
              <Component {...getProps(cardProps)} />
            </W>
          )
        })}
      </div>
      </LayoutGroup>
      </CardMinimizedProvider>
      </CardRemoveProvider>
    </div>
  )
}
